import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, CreditCard, ExternalLink, Receipt, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanEntitlement } from '../../hooks/usePlanEntitlement';
import { formatUsd } from '../../data/pricingPlans';
interface BillingManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface BillingRecord {
  date: string;
  plan?: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description?: string;
  reference?: string;
  receipt_url?: string;
}
const STATUS_STYLE: Record<BillingRecord['status'], string> = {
  paid: 'border-green-500/40 bg-green-500/10 text-green-400',
  pending: 'border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]',
  failed: 'border-[#FF0000]/40 bg-[#FF0000]/10 text-[#FF0000]'
};
/**
 * Billing history for the authenticated user. Records come from the billing
 * Edge Function only — nothing is fabricated locally, so an account with no
 * payments shows a clean empty state.
 */
export function BillingManagementModal({
  isOpen,
  onClose
}: BillingManagementModalProps) {
  const {
    user
  } = useAuth();
  const {
    planLabel,
    priceLabel,
    statusLabel,
    renewalDate
  } = usePlanEntitlement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<BillingRecord[]>([]);
  const fetchBillingData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`https://neadfoxscgjeekvabnoz.supabase.co/functions/v1/billing-history?user_id=${user.id}`);
      if (!response.ok) throw new Error('Request failed');
      const data = await response.json();
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setHistory([]);
      setError('Could not load billing history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  useEffect(() => {
    if (isOpen) fetchBillingData();
  }, [isOpen, fetchBillingData]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-1840" data-mp="1bk9n2p891k2u">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6" data-id="element-1841" data-mp="1bl2n77cpdbc0">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-black/85 backdrop-blur-sm" data-id="element-1842" data-mp="1gil0tb1o0eqc" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="billing-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-gray-800 bg-[#050505] p-6 sm:p-8" data-id="element-1843" data-mp="gbz2x23fty5c">
            <button type="button" onClick={onClose} aria-label="Close billing history" className="absolute right-4 top-4 p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-1844" data-mp="3etuvkujtneub">
              <X size={18} data-id="element-1845" data-mp="8arsnljrl4gb" />
            </button>

            <header className="mb-7 pr-10" data-id="element-1846" data-mp="3qqc1pwj3ikut">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-1847" data-mp="3qpaqns22xyd3">
                Transaction log
              </p>
              <h2 id="billing-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-1848" data-mp="3gs60lh545ir0">
                Billing History
              </h2>
            </header>

            {/* Current plan summary */}
            <div className="mb-7 border border-gray-800 bg-[#0a0a0a] p-5" data-id="element-1849" data-mp="37tas1yeljwkj">
              <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1850" data-mp="3btbi6gb25w7f">
                <div data-id="element-1851" data-mp="160wcv73ft67t">
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-1852" data-mp="160mmvjie6z24">
                    Current plan
                  </p>
                  <p className="mt-1.5 text-lg font-black uppercase tracking-tight text-white" data-id="element-1853" data-mp="2uizaft10bwms">
                    {planLabel}
                  </p>
                  <p className="mt-1 font-mono text-xs text-gray-400" data-id="element-1854" data-mp="2qjqdygel1knj">
                    {priceLabel} · {statusLabel}
                  </p>
                </div>
                {renewalDate && <div className="flex items-center gap-2 font-mono text-xs text-gray-400" data-id="element-1855" data-mp="2zh92vvvckj4p">
                    <Calendar size={14} className="text-gray-600" data-id="element-1856" data-mp="3e5n6wa4i2dmu" />
                    Renews {renewalDate}
                  </div>}
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-gray-800 pt-4" data-id="element-1857" data-mp="3n73hahvbefr2">
                <div className="flex items-center gap-3" data-id="element-1858" data-mp="3ia4qo9av0qxm">
                  <CreditCard size={16} className="text-gray-600" data-id="element-1859" data-mp="1su5baagne59j" />
                  <div data-id="element-1860" data-mp="1sudok3goncl9">
                    <p className="font-mono text-xs text-white" data-id="element-1861" data-mp="1svyflj3sgded">
                      Payment method
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-1862" data-mp="3nxtgrmhat1y8">
                      Managed via Paystack
                    </p>
                  </div>
                </div>
                <a href="https://paystack.com/pay/update-payment" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF0000] transition-colors duration-150 ease-out hover:text-white" data-id="element-1863" data-mp="1ptwym4wxdz5">
                  Update
                  <ExternalLink size={12} data-id="element-1864" data-mp="kg1o05vc8dyz" />
                </a>
              </div>
            </div>

            {loading ? <p className="border border-gray-800 bg-[#0a0a0a] py-10 text-center font-mono text-xs uppercase tracking-widest text-gray-500" data-id="element-1865" data-mp="pecx2edt6kg5">
                Loading billing history…
              </p> : error ? <div className="border border-[#FF0000]/30 bg-[#FF0000]/5 p-6 text-center" data-id="element-1866" data-mp="ye9q0s78e5h0">
                <p className="font-mono text-xs text-[#FF0000]" data-id="element-1867" data-mp="2izoke24lzriv">{error}</p>
                <button type="button" onClick={fetchBillingData} className="mt-4 border border-gray-700 px-5 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-white transition-colors duration-200 ease-out hover:bg-white/5" data-id="element-1868" data-mp="2dyipbxnh7s66">
                  Retry
                </button>
              </div> : history.length === 0 ? <div className="border border-gray-800 bg-[#0a0a0a] px-6 py-12 text-center" data-id="element-1869" data-mp="2xpdzl19jdw25">
                <Receipt size={28} aria-hidden="true" className="mx-auto text-gray-700" data-id="element-1870" data-mp="2suq9ixyvwtlg" />
                <p className="mt-4 text-sm font-black uppercase tracking-wide text-white" data-id="element-1871" data-mp="3ld3vo0auyr4p">
                  No payments yet
                </p>
                <p className="mt-2 font-mono text-xs text-gray-500" data-id="element-1872" data-mp="3gk2etmr3chih">
                  Your completed purchases will appear here.
                </p>
              </div> : <ul className="border border-gray-800" data-id="element-1873" data-mp="2tqvkkx0v2s50">
                {history.map((item, index) => <li key={item.reference ?? `${item.date}-${index}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-900 bg-[#0a0a0a] p-4 last:border-b-0" data-id="element-1874" data-mp="2f27o8oj6yee0" data-mpi={index}>
                    <div className="min-w-0" data-id="element-1875" data-mp="3cookdqg6uly6">
                      <p className="text-sm font-bold text-white" data-id="element-1876" data-mp="3cndgzrqnng7l">
                        {item.plan || item.description || 'Purchase'}
                      </p>
                      <p className="mt-1 font-mono text-[11px] text-gray-500" data-id="element-1877" data-mp="32p7ep90xs827">
                        {new Date(item.date).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                        {item.reference ? ` · ${item.reference}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-1878" data-mp="1eryaukcctbxa">
                      <span className="font-mono text-sm font-bold text-white" data-id="element-1879" data-mp="1tpqplaa2twaf">
                        {formatUsd(item.amount)}
                      </span>
                      <span className={`border px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${STATUS_STYLE[item.status] ?? STATUS_STYLE.pending}`} data-id="element-1880" data-mp="1oojildfrd14t">
                        {item.status}
                      </span>
                      {item.receipt_url && <a href={item.receipt_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 transition-colors duration-150 ease-out hover:text-white" data-id="element-1881" data-mp="15zmkszl5jqhu">
                          Receipt
                        </a>}
                    </div>
                  </li>)}
              </ul>}

            <button type="button" onClick={onClose} className="mt-7 w-full border border-gray-700 py-3 font-mono text-[11px] font-black uppercase tracking-[0.15em] text-white transition-colors duration-200 ease-out hover:bg-white/5" data-id="element-1882" data-mp="369hvcd0lq3hz">
              Close
            </button>
          </motion.div>
        </div>}
    </AnimatePresence>;
}